import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Link as LinkIcon, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";

/**
 * Client-level Links — generic URL list. Used for Drive folders, GST portal
 * shortcuts, MCA links, any company-level reference URL the team needs
 * one-click access to.
 */
export default function ClientLinksTab({ clientId, links, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ url: "", title: "" });

  const save = async () => {
    try {
      await api.post(`/clients/${clientId}/links`, form);
      toast.success("Link added");
      setOpen(false);
      setForm({ url: "", title: "" });
      onChanged?.();
    } catch (e) { toast.error(parseApiError(e, "Failed to add link")); }
  };

  const remove = async (link) => {
    if (!window.confirm(`Remove this link?\n\n${link.title}`)) return;
    try {
      await api.delete(`/clients/${clientId}/links/${link.id}`);
      onChanged?.();
    } catch (e) { toast.error(parseApiError(e, "Delete failed")); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-slate-500 font-mono-data">{(links || []).length} links</div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-new-link">
                <Plus size={14} className="mr-1.5" /> Add Link
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>Add Link</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title (optional)</Label>
                  <Input
                    className="mt-1 rounded-sm"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g. Client Drive folder · GST Portal Login"
                    data-testid="input-link-title"
                  />
                </div>
                <div>
                  <Label className="text-xs">URL *</Label>
                  <Input
                    className="mt-1 rounded-sm font-mono-data text-xs"
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    placeholder="https://"
                    data-testid="input-link-url"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={save} disabled={!form.url.trim()} data-testid="btn-save-link">
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-1.5" data-testid="client-links-list">
        {(links || []).length === 0 && (
          <div className="text-xs text-slate-400 text-center py-8 border border-dashed border-slate-200">
            No links yet. Add Drive folders, portal logins, or any reference URL the team needs.
          </div>
        )}
        {(links || []).map((l) => (
          <div
            key={l.id || l.url}
            className="flex items-center justify-between px-3 py-2 border border-slate-200 hover:border-[#0a2540] group"
            data-testid={`client-link-${l.id || l.url}`}
          >
            <a
              href={l.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 min-w-0 flex-1"
            >
              <ExternalLink size={14} className="text-slate-400 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-[#0a2540] hover:underline truncate">{l.title || l.url}</div>
                {l.title && l.title !== l.url && (
                  <div className="text-[10px] text-slate-400 font-mono-data truncate">{l.url}</div>
                )}
              </div>
            </a>
            {canEdit && (
              <button
                onClick={() => remove(l)}
                className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 inline-flex items-center justify-center text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200 rounded-sm shrink-0"
                title="Remove link"
                data-testid={`btn-delete-link-${l.id || l.url}`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
